import { useTranslation } from 'react-i18next';

import {
	WalletOperationOutcome,
	WalletOperationQueue,
	WalletOperationRecordKind,
	WalletOperationRefusalCode,
	WalletOperationResolutionAction,
	WalletOperationResolutionResult,
	WalletOperationStage,
	WalletOperationState,
	zWalletOperationKind,
} from 'lib/models/walletOperations';

/**
 * The Spanish copy for the operation vocabulary.
 *
 * Each value is switched to a literal translation key rather than composed, so the keys
 * stay greppable and typed against the resource file, as they are everywhere else here.
 */
function useWalletOperationLabels() {
	const { t } = useTranslation();

	/**
	 * What the operator is told an operation is.
	 *
	 * The kind is read back through the schema rather than switched on directly, so the switch
	 * stays exhaustive over the kinds this release knows while a kind it does not know is shown
	 * as the API named it. That raw string is the one thing worth saying about such a row: it is
	 * what support would quote when asking what it is.
	 *
	 * @param kind The kind as it arrived on the record
	 */
	function kindLabel(kind: WalletOperationRecordKind): string {
		const known = zWalletOperationKind.safeParse(kind);

		if (!known.success) return kind;

		switch (known.data) {
			case 'order.charge':
				return t('common:walletOperationKindCharge');
			case 'order.refund':
				return t('common:walletOperationKindRefund');
			case 'postnet.intention':
				return t('common:walletOperationKindPostnetIntention');
			case 'transfer.cardholder':
				return t('common:walletOperationKindTransferCardholder');
			case 'transfer.cbu':
				return t('common:walletOperationKindTransferCbu');
		}
	}

	function stateLabel(state: WalletOperationState): string {
		switch (state) {
			case 'claimed':
				return t('common:walletOperationStateClaimed');
			case 'prepared':
				return t('common:walletOperationStatePrepared');
			case 'sent':
				return t('common:walletOperationStateSent');
			case 'settled':
				return t('common:walletOperationStateSettled');
			case 'failed':
				return t('common:walletOperationStateFailed');
			case 'unresolved':
				return t('common:walletOperationStateUnresolved');
			case 'resolved':
				return t('common:walletOperationStateResolved');
		}
	}

	function outcomeLabel(outcome?: WalletOperationOutcome): string {
		switch (outcome) {
			case 'approved':
				return t('common:walletOperationOutcomeApproved');
			case 'rejected':
				return t('common:walletOperationOutcomeRejected');
			case 'pending':
				return t('common:walletOperationOutcomePending');
			case 'unknown':
				return t('common:walletOperationOutcomeUnknown');
			default:
				return t('common:walletOperationOutcomeNone');
		}
	}

	function stageLabel(stage: WalletOperationStage): string {
		switch (stage) {
			case 'checking':
				return t('common:walletOperationStageChecking');
			case 'unresolved':
				return t('common:walletOperationStageUnresolved');
		}
	}

	/** The tab, which is the question the screen was opened with. */
	function queueLabel(queue: WalletOperationQueue): string {
		switch (queue) {
			case 'review':
				return t('common:walletOperationsQueueReview');
			case 'checking':
				return t('common:walletOperationsQueueChecking');
			case 'closed':
				return t('common:walletOperationsQueueClosed');
			case 'all':
				return t('common:walletOperationsQueueAll');
		}
	}

	/** What that tab is listing, said once under it rather than guessed at from the label. */
	function queueHelp(queue: WalletOperationQueue): string {
		switch (queue) {
			case 'review':
				return t('common:walletOperationsQueueReviewHelp');
			case 'checking':
				return t('common:walletOperationsQueueCheckingHelp');
			case 'closed':
				return t('common:walletOperationsQueueClosedHelp');
			case 'all':
				return t('common:walletOperationsQueueAllHelp');
		}
	}

	function resolutionActionLabel(action: WalletOperationResolutionAction): string {
		switch (action) {
			case 'settle':
				return t('common:walletOperationActionComplete');
			case 'resolve':
				return t('common:walletOperationActionResolve');
			case 'recheck':
				return t('common:walletOperationActionRecheck');
		}
	}

	function resolutionResultLabel(result: WalletOperationResolutionResult): string {
		switch (result) {
			case 'settled':
				return t('common:walletOperationResultSettled');
			case 'failed':
				return t('common:walletOperationResultFailed');
			case 'pending':
				return t('common:walletOperationResultPending');
			case 'resolved':
				return t('common:walletOperationResultResolved');
		}
	}

	/**
	 * What the operator is told for each way the API refuses a manual resolution.
	 *
	 * Each code is its own sentence, because each one is a different thing to do next: type
	 * a reason, reload the row, wait for the other operator, stop asking the provider, or
	 * escalate a kind nothing can complete. A refusal this screen does not know is shown
	 * with the API's own message, and a failure that carries neither says only that.
	 *
	 * @param code The code the API refused with, if it sent one
	 * @param message The message the API sent, if it sent one
	 */
	function refusalMessage(code: WalletOperationRefusalCode | null, message: string | null): string {
		switch (code) {
			case 'operation.reason_required':
				return t('common:walletOperationRefusedReasonRequired');
			case 'operation.not_in_review':
				return t('common:walletOperationRefusedNotInReview');
			case 'operation.locked':
				return t('common:walletOperationRefusedLocked');
			case 'operation.not_pollable':
				return t('common:walletOperationRefusedNotPollable');
			case 'operation.not_settleable':
				return t('common:walletOperationRefusedNotSettleable');
			default:
				return message || t('common:walletOperationRefusedUnknown');
		}
	}

	return {
		kindLabel,
		stateLabel,
		outcomeLabel,
		stageLabel,
		queueLabel,
		queueHelp,
		resolutionActionLabel,
		resolutionResultLabel,
		refusalMessage,
	};
}

export default useWalletOperationLabels;
